import cn, { Argument as ClassName } from 'classnames'
import React, { memo, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'

import { CheckboxInput } from '../../../../lib/checkbox-input'
import { isSupportDomain, isPartnerDomain } from '../../../../lib/currentDomain'
import { WidgetLayout } from '../../../../lib/widget-layout'
import {
    MeasurementDeviceExtendedGetModel,
    UpdateMeasurementDeviceModel,
    useMeasurementDevicesUpdateMutation,
    useCompanyGetUsersQuery,
    usePartnerGetAllAllQuery,
    usePartnerGetCompaniesByPartnerIdQuery,
} from '../../../redux/api/web-api'
import { notifyError, notifySuccess } from '../../notificationsManager/Notify'
import { Combobox } from '../../../components/combobox'
import Input from '../../../../common/components/Input'
import TextArea from '../../../../common/components/TextArea'
import useText from '../../../hooks/useText'

export type MeasurementDeviceFormWidgetProps = {
    device: MeasurementDeviceExtendedGetModel
    className?: ClassName
    style?: React.CSSProperties
}

export const MeasurementDeviceFormWidget = memo(function MeasurementDeviceFormWidget({
    device: origin,
    className,
    style,
}: MeasurementDeviceFormWidgetProps): React.ReactElement {
    const text = useText()
    const [updateMeasurementDevice] = useMeasurementDevicesUpdateMutation()
    const save = async (device: UpdateMeasurementDeviceModel) => {
        try {
            await updateMeasurementDevice({
                id: origin.id || 0,
                updateMeasurementDeviceModel: device,
            }).unwrap()
            notifySuccess(text('msgMeasurementDeviceWasUpdated'))
        } catch (_) {
            notifyError(text('msgMeasurementDeviceCouldNotBeUpdated'))
        }
    }

    const {
        handleSubmit,
        register,
        setValue,
        watch,
        control,
        reset,
        formState: { isSubmitting, isDirty, isSubmitSuccessful },
    } = useForm<MeasurementDeviceExtendedGetModel>({
        values: {
            ...origin,
            name: origin.name || origin.serialNumber,
        },
    })

    const partnerIdWatch = watch('partnerId')
    const companyIdWatch = watch('companyId')
    const loadBalancerGroupNameWatch = watch('loadBalancerGroupName')

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({}, { keepValues: true })
        }
    }, [isSubmitSuccessful, reset])

    const { data: partners } = usePartnerGetAllAllQuery({ excludeOrphans: false }, { skip: !isSupportDomain })

    const { data: users } = useCompanyGetUsersQuery(
        {
            companyId: companyIdWatch || '',
        },
        {
            skip: !companyIdWatch,
            refetchOnMountOrArgChange: true,
        }
    )

    const { data: companies } = usePartnerGetCompaniesByPartnerIdQuery(
        {
            partnerId: partnerIdWatch || -1,
        },
        {
            skip: !partnerIdWatch,
            refetchOnMountOrArgChange: true,
        }
    )
    return (
        <WidgetLayout
            title={text('lblMeasurementDevicePage', { name: origin.serialNumber })}
            className={cn(className)}
            style={style}
            data-testid={'detailed-form-widget'}
        >
            <form name="form" role="form" noValidate onSubmit={handleSubmit(save)}>
                <div className="flex-1">
                    <Input id="input-name" data-testid="input-name" labelText={text('lblName')} {...register('name')} />
                </div>

                <div className="flex-1">
                    <Input
                        id="input-description"
                        data-testid="input-desc"
                        labelText={text('lblDescriptionField')}
                        {...register('description')}
                    />
                </div>

                <div className="flex-1">
                    <TextArea
                        id="input-detailedDescription"
                        data-testid="input-detailed-desc"
                        labelText={text('lblDetailedDescriptionField')}
                        {...register('detailedDescription')}
                        rows={5}
                        cols={5}
                    />
                </div>

                <div className="flex-1">
                    <Input
                        id="input-pointOfInstallation"
                        data-testid="input-point"
                        labelText={text('lblPointOfInstallationField')}
                        {...register('pointOfInstallation')}
                    />
                </div>

                {isPartnerDomain || isSupportDomain ? (
                    <>
                        {isSupportDomain ? (
                            <div className="flex-1">
                                <Controller
                                    name="partnerId"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Combobox
                                            id="input-partnerId"
                                            data-testid="input-partnerId"
                                            labelText={text('lblPartnerField')}
                                            options={partners?.map(({ id, name }) => ({
                                                value: id || '',
                                                label: name || '',
                                            }))}
                                            disabled={loadBalancerGroupNameWatch !== null}
                                            value={value}
                                            onChange={(option) => {
                                                onChange(option.value)
                                                // weird workaround, undefined not resetting input sometimes. probably react-hook-form issue
                                                setValue('companyId', null as unknown as undefined)
                                                setValue('chargePointUserId', null as unknown as undefined)
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        ) : null}

                        <div className="flex-1">
                            <Controller
                                name="companyId"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Combobox
                                        id="input-company"
                                        data-testid="input-org"
                                        labelText={text('lblOrganisationField')}
                                        options={companies?.map(({ id, name }) => ({
                                            value: id || '',
                                            label: name || '',
                                        }))}
                                        disabled={loadBalancerGroupNameWatch !== null || !partnerIdWatch}
                                        value={value}
                                        onChange={(option) => {
                                            onChange(option.value)
                                            // weird workaround, undefined not resetting input sometimes. probably react-hook-form issue
                                            setValue('chargePointUserId', null as unknown as undefined)
                                        }}
                                    />
                                )}
                            />
                        </div>

                        <div className="flex-1">
                            <Controller
                                name="chargePointUserId"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Combobox
                                        id="input-chargePointUserId"
                                        data-testid="input-user"
                                        labelText={text('lblUserField')}
                                        disabled={!companyIdWatch}
                                        options={users?.map(({ id, fullName }) => ({
                                            value: id || '',
                                            label: fullName || '',
                                        }))}
                                        value={value}
                                        onChange={(option) => onChange(option.value)}
                                    />
                                )}
                            />
                        </div>
                    </>
                ) : null}

                <div className="flex-1">
                    <Controller
                        name="ignoreCurrentDirection"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <CheckboxInput
                                checked={!value}
                                setChecked={(checked) => onChange(!checked)}
                                dataTestId="checkbox-ignoreCurrentDirection"
                                label={text('lblSystemWithPowerGenerators')}
                            />
                        )}
                    />
                </div>
                <div className="flex-1">
                    <Controller
                        name="active"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <CheckboxInput
                                checked={value}
                                setChecked={onChange}
                                dataTestId={'checkbox-measurementDeviceActive'}
                                label={text('lblActive')}
                            />
                        )}
                    />
                </div>
                <div className="pull-right">
                    <Button
                        loading={isSubmitting}
                        disabled={isSubmitting || !isDirty || (isPartnerDomain ? !companyIdWatch : false)}
                        type="submit"
                        label={text('btnSave')}
                        icon="pi pi-save"
                        data-testid="btn-save-detailed"
                    />
                </div>
            </form>
        </WidgetLayout>
    )
})
